/**
 * Canvas frame recorder (IO-01) — MediaRecorder → WebM download.
 * See docs/architecture/DESIGNS-TRACK-X.md §3.
 */

export type RecorderMime = string | null;

/** Pick best supported WebM mime type. */
export function pickRecorderMime(): RecorderMime {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ];
  for (const m of candidates) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return null;
}

export function recorderSupported(): boolean {
  return (
    pickRecorderMime() !== null && typeof HTMLCanvasElement !== "undefined"
  );
}

export class FrameRecorder {
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private _recording = false;

  get recording(): boolean {
    return this._recording;
  }

  /**
   * Start capturing `canvas` at `fps` (default 30).
   * Throws if unsupported.
   */
  start(canvas: HTMLCanvasElement, fps = 30): void {
    if (this._recording) this.stop();
    const mime = pickRecorderMime();
    if (!mime)
      throw new Error("MediaRecorder / WebM not supported in this browser");

    this.stream = canvas.captureStream(fps);
    this.chunks = [];
    const rec = new MediaRecorder(this.stream, {
      mimeType: mime,
      videoBitsPerSecond: 4_000_000,
    });
    rec.ondataavailable = (ev) => {
      if (ev.data.size > 0) this.chunks.push(ev.data);
    };
    rec.onstop = () => {
      this.finishDownload(mime);
      this.teardown();
    };
    this.recorder = rec;
    this._recording = true;
    rec.start(200); // timeslice ms
  }

  /** Stop and trigger download when the final blob is ready. */
  stop(): void {
    if (!this.recorder || !this._recording) return;
    this._recording = false;
    if (this.recorder.state !== "inactive") {
      this.recorder.stop();
    } else {
      this.teardown();
    }
    // stop tracks
    this.stream?.getTracks().forEach((t) => t.stop());
  }

  private finishDownload(mime: string): void {
    if (this.chunks.length === 0) return;
    const blob = new Blob(this.chunks, { type: mime.split(";")[0] });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    a.href = url;
    a.download = `showoff-${stamp}.webm`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private teardown(): void {
    this.recorder = null;
    this.stream = null;
    this.chunks = [];
    this._recording = false;
  }
}
